/**
 * Draws the rows of suggested products.
 *
 *   <recs-rail data-job="similar|recent|discover|home|bestsellers">   snippets/recs-rail.liquid
 *   <complete-look>                                        blocks/complete-look.liquid
 *   <bag-upsell>                                           snippets/cart-extras.liquid ("Complete your order")
 *
 * The rules are in assets/kz-recs.js; what the shopper did is in assets/kz-signals.js. This
 * file loads the product index once per session (kept in sessionStorage, refreshed hourly),
 * asks the rules what each row should hold, and builds the cards from the page's own card
 * template, so a card here is the same card as in a collection grid. The index is only asked
 * for when a row needs it: the rows in the bag wait until the bag is opened.
 *
 * Nothing shows until the index has arrived and a row is known to have enough products. A row
 * then appears only where it cannot make the page jump: below the screen, or as the answer
 * to a tap. "Complete the look" keeps its room from the first paint when the page can tell it
 * will have pieces, and fades them in there. With ?recs=debug in the address every card says
 * why it is there.
 *
 * window.kz.recs(job, context) returns a promise of [{ h, reason, score, why }].
 */
import { build, recs } from './kz-recs.js';
import * as signals from './kz-signals.js';

const config = JSON.parse(document.querySelector('[data-kz-config]')?.textContent || 'null');
const debug = new URLSearchParams(window.location.search).get('recs') === 'debug';
const WIDTHS = [200, 300, 400, 500, 600, 800, 1000];
const sized = (address, width) => address.replace(/([?&]width=)\d+/, `$1${width}`);
const kept = (run) => {
  try {
    return run();
  } catch (error) {
    return null;
  }
};

async function loadIndex() {
  const key = `kz_index:${config.locale}`;
  const saved = kept(() => JSON.parse(sessionStorage.getItem(key)));
  if (saved?.stamp === config.stamp) return build(saved.pages);
  const pages = [];
  for (let page = 1, last = 1; page <= last; page += 1) {
    const address = `${config.index}${page > 1 ? `&page=${page}` : ''}`;
    // One more try after two seconds: a single lost request should not cost him the rows.
    const response = await fetch(address).catch(() => null);
    const answer = response?.ok ? response : await new Promise((resolve) => window.setTimeout(resolve, 2000)).then(() => fetch(address));
    if (!answer.ok) throw new Error(`Product index failed (${answer.status})`);
    pages.push(JSON.parse(await answer.text()));
    last = pages[0].pages;
  }
  kept(() => sessionStorage.setItem(key, JSON.stringify({ stamp: config.stamp, pages })));
  return build(pages);
}

let loading;
/** The product index, asked for the first time a row needs it. */
const getIndex = () => {
  if (!loading) {
    loading = loadIndex();
    loading.catch((error) => {
      console.error('Kanze: suggestions are off for this page.', error);
      // The rows are simply not there: only this says so (the tracking plan's canary).
      count('Recommendations Failed', { reason: /\((\d+)\)/.test(error?.message || '') ? `index_${error.message.match(/\((\d+)\)/)[1]}` : 'index_unreadable' });
    });
  }
  return loading;
};

/** Says what happened, to the theme's tracking (assets/kz-track.js, docs/design/WEB-TRACKING-PLAN.md). */
const count = (name, props) => window.kz?.track?.(name, props);
/** Runs once, when a row has been at least half on screen for a second (the "seen" events). */
const whenSeen = (row, key, run) => window.kz?.seen?.(row, key, run);
/** The reasons the pieces of a row were chosen for, each named once. */
const reasonsIn = (nodes) => [...new Set([...nodes].map((node) => node.dataset.reason).filter(Boolean))];
let bag = config?.bag || [];

/** What every job needs to know about this page. */
function context(data, extra) {
  const viewed = data.signals.viewed.map((entry) => data.index.by[entry.h]).filter(Boolean);
  const womens = viewed.filter((p) => p.g === 'w').length;
  const gender = data.index.by[config.product]?.g || config.gender || (womens > viewed.length / 2 ? 'w' : 'm');
  // A collection page can change category in place (assets/collection.js).
  const category = document.querySelector('collection-page')?.dataset.category ?? config.category;
  return { product: config.product, category, bag, gender, held: Boolean(config.held), now: Date.now(), ...extra };
}

window.kz = Object.assign(window.kz || {}, {
  signals,
  // The product index itself, for what else is worked out from it (assets/kz-siblings.js).
  index: () => getIndex(),
  recs: async (job, extra = {}) => {
    const data = { index: await getIndex(), signals: signals.read() };
    return recs(job, context(data, extra), data);
  },
});

/**
 * Shows a row without making the page jump. Until then it is a box with no height.
 * It may appear while it is still below the screen, or as the answer to a tap or a key. Not
 * by itself while it is on screen, and not once it is above the screen: what sits under it
 * would be pushed down in front of him.
 */
function reveal(row, { tap = true } = {}) {
  if (!row.classList.contains('is-waiting')) return;
  const stop = whenBelow(row, show);
  function show() {
    stop();
    window.removeEventListener('pointerdown', show, true);
    window.removeEventListener('keydown', show, true);
    row.classList.remove('is-waiting');
  }
  if (!tap) return;
  window.addEventListener('pointerdown', show, true);
  window.addEventListener('keydown', show, true);
}

/** Runs once, the first time a box is wholly below the screen. Returns how to stop waiting. */
function whenBelow(box, run) {
  const watcher = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting || entry.boundingClientRect.top < (entry.rootBounds?.bottom ?? window.innerHeight)) return;
    watcher.disconnect();
    run();
  });
  watcher.observe(box);
  return () => watcher.disconnect();
}

/** How long a row takes to fade in over the blocks that held its place. */
const FADE = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 160;
const fade = (box, from, to) => box.animate([{ opacity: from }, { opacity: to }], { duration: FADE, easing: 'ease' }).finished.catch(() => {});

/** The words for a row: its title and the one line that says why these products. */
function words(row, items, job) {
  const { titles, lines } = config.strings;
  const first = items[0]?.reason;
  const counts = {};
  for (const item of items) counts[item.reason] = (counts[item.reason] || 0) + 1;
  const top = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  let line = lines[job] || lines[first] || '';
  if (first === 'bestseller') line = lines.bestseller;
  else if (job === 'similar') line = top.slice(0, 2).map((reason, at) => (at ? lines[reason].toLowerCase() : lines[reason])).join(lines.join);
  else if (job === 'complete') line = lines[first];
  row.querySelector('[data-title]').textContent = first === 'bestseller' ? titles.bestseller : titles[job] || titles.discover;
  row.querySelector('[data-line]').textContent = line;
}

const label = (item) => Object.assign(document.createElement('span'), { className: 'recs__debug', textContent: `${item.reason} · ${item.score} · ${item.why}` });

/** One product card, built from the page's card template and the product's index record. */
function card(p, item, job) {
  const node = document.querySelector('template[data-kz-card]').content.firstElementChild.cloneNode(true);
  const slot = (name) => node.querySelector(`[data-slot="${name}"]`);
  const id = `CardTitle-${job}-${p.id}`;
  node.dataset.reason = item.reason;
  node.dataset.handle = p.h;
  node.querySelector('.card').setAttribute('aria-labelledby', id);
  for (const [name, source] of [['image', p.img], ['image2', p.img2]]) {
    const image = slot(name);
    if (!image) continue;
    if (!source) image.remove();
    else Object.assign(image, { srcset: WIDTHS.map((width) => `${sized(source, width)} ${width}w`).join(', '), src: sized(source, 600) });
  }
  slot('media-link').href = p.url;
  const title = slot('title');
  Object.assign(title, { href: p.url, textContent: p.t });
  title.parentElement.id = id;
  const quick = slot('quick-add');
  if (quick) {
    quick.dataset.quickAdd = p.url;
    quick.setAttribute('aria-label', quick.getAttribute('aria-label').replace('[title]', p.t));
  }
  const fit = slot('fit');
  if (fit && p.fl) fit.textContent = p.fl;
  else fit?.remove();
  slot('price').insertAdjacentHTML('afterbegin', `${p.ph} `);
  const saving = slot('saving');
  if (p.sv) saving.textContent = p.sv;
  else saving.remove();
  const dots = slot('dots');
  if (dots && p.cv.length) {
    const sample = slot('dot');
    const more = slot('dots-more');
    for (const [name, , address, photo, inStock, shown, saved] of p.cv.slice(0, config.dots)) {
      const dot = sample.cloneNode(true);
      const button = dot.querySelector('[data-color-dot]');
      const paint = dot.querySelector('.dots__dot');
      const reader = dot.querySelector('swatch-dot');
      button.dataset.url = address;
      if (photo) button.dataset.image = sized(photo, 600);
      if (!inStock) paint.classList.add('dots__dot--unavailable');
      if (saved) {
        paint.style.cssText = saved;
        reader.remove();
      } else {
        Object.assign(reader.dataset, { name, text: `${p.ty} ${p.t}` });
        if (photo) reader.dataset.image = photo;
      }
      dot.querySelector('.visually-hidden').textContent = shown;
      dots.insertBefore(dot, sample);
    }
    sample.remove();
    // How many colours the row leaves out, for what is added to it later (assets/kz-siblings.js).
    dots.dataset.extra = Math.max(0, p.cv.length - config.dots);
    if (p.cv.length > config.dots) more.textContent = more.dataset.text.replace('[count]', window.Kanze.digits(p.cv.length - config.dots));
    else more.remove();
  } else dots?.remove();
  if (debug) node.append(label(item));
  return node;
}

/** A suggested product that sat half on screen for a second counts as seen; a tap on it clears that. */
const seen = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      const node = entry.target;
      window.clearTimeout(node.seenTimer);
      if (!entry.isIntersecting) continue;
      node.seenTimer = window.setTimeout(() => {
        seen.unobserve(node);
        signals.impression(node.dataset.handle);
      }, 1000);
    }
  },
  { threshold: 0.5 }
);
function track(node, job) {
  seen.observe(node);
  node.addEventListener('click', (event) => {
    seen.unobserve(node);
    window.clearTimeout(node.seenTimer);
    signals.impression(node.dataset.handle, true);
    // Opened (a tap on its photo or its name), not a size chosen or "+" pressed in it.
    if (job && event.target instanceof Element && event.target.closest('a[href]')) {
      count('Recommendation Clicked', { job, reason: node.dataset.reason, position: [...node.parentElement.children].indexOf(node) + 1, product_handle: node.dataset.handle });
    }
  });
}

/** How the size chosen in a row came to be chosen (the tracking plan's `size_source`). */
const sizeSourceIn = (row) => (row.querySelectorAll('input[type="radio"]').length <= 1 ? 'single_size' : row.dataset.sizeBy || 'manual');
/** For an add made from rows: each variant with how its size was chosen. */
const sizesIn = (rows) => Object.fromEntries(rows.map((row) => [Number(row.querySelector('input:checked').value), sizeSourceIn(row)]));
if (typeof document !== 'undefined') {
  document.addEventListener('change', (event) => {
    const row = event.target instanceof Element ? event.target.closest('[data-look-row]') : null;
    if (row && event.isTrusted) row.dataset.sizeBy = 'manual';
  });
}

class RecsRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.previous = this.querySelector('[data-previous]');
    this.next = this.querySelector('[data-next]');
    this.previous.addEventListener('click', () => this.move(-1));
    this.next.addEventListener('click', () => this.move(1));
    this.track.addEventListener('scroll', () => window.requestAnimationFrame(() => this.arrows()), { passive: true });
    new ResizeObserver(() => this.arrows()).observe(this.track);
  }

  move(direction) {
    const reading = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    this.track.scrollBy({ left: direction * reading * this.track.clientWidth, behavior: 'smooth' });
  }

  /** Each arrow switches off at its end of the row. */
  arrows() {
    const scrolled = Math.abs(this.track.scrollLeft);
    const room = this.track.scrollWidth - this.track.clientWidth;
    this.previous.disabled = scrolled <= 4;
    this.next.disabled = scrolled >= room - 4;
  }

  pick(data, taken) {
    this.index = data.index;
    this.items = recs(this.dataset.job, context(data, { limit: Number(this.dataset.limit) || 8, fallback: this.hasAttribute('data-fallback'), taken }), data);
  }

  draw() {
    const items = this.items.length >= 2 ? this.items : [];
    const key = items.map((item) => item.h).join();
    if (key === this.drawn) return;
    this.drawn = key;
    if (!items.length) {
      this.classList.add('is-waiting');
      return;
    }
    words(this, items, this.dataset.job);
    this.track.replaceChildren(...items.map((item) => card(this.index.by[item.h], item, this.dataset.job)));
    this.track.scrollLeft = 0;
    for (const node of this.track.children) track(node, this.dataset.job);
    this.arrows();
    const job = this.dataset.job;
    whenSeen(this, `recs:${job}:${place(this)}`, () => count('Recommendation Shown', { job, count: this.track.children.length, reasons: reasonsIn(this.track.children), product_handle: config.product || undefined }));
    // The last thing in a panel moves nothing when it arrives: it may show at once.
    if (this.hasAttribute('data-inline')) this.classList.remove('is-waiting');
    else reveal(this);
  }
}

/**
 * One compact row for the product at this address, rendered by Shopify
 * (sections/complete-look-item.liquid): photo, title, price, sizes.
 */
async function pieceRow(address) {
  const response = await fetch(`${address}${address.includes('?') ? '&' : '?'}section_id=complete-look-item`);
  if (!response.ok) return null;
  const parsed = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-look-row]');
  if (!parsed) return null;
  const row = document.importNode(parsed, true);
  // The photo is ready before the row shows, so it fades in with it. It is not waited for long.
  const photo = row.querySelector('img');
  if (photo) {
    photo.loading = 'eager';
    await Promise.race([photo.decode().catch(() => {}), new Promise((resolve) => window.setTimeout(resolve, 1500))]);
  }
  return row;
}

/** The row of a suggested piece, marked with why it was chosen. */
async function lookRow(index, item) {
  const row = await pieceRow(index.by[item.h].url);
  if (!row) return null;
  row.dataset.reason = item.reason;
  row.dataset.handle = item.h;
  if (debug) row.append(label(item));
  return row;
}

/** With saved "Find my size" answers and a ready Fit Table, his size is chosen in the row. */
async function suggestSize(row) {
  const source = row.querySelector('[data-fit-table]');
  if (!source) return;
  try {
    const { recommendFromSaved, norm } = await import(source.dataset.module);
    const result = recommendFromSaved(JSON.parse(source.textContent));
    const input = result?.ok && [...row.querySelectorAll('input[type="radio"]')].find((radio) => norm(radio.dataset.size) === norm(result.size));
    if (input && !input.disabled && !row.querySelector('input:checked')) {
      input.checked = true;
      row.dataset.sizeBy = 'find_my_size';
    }
  } catch (error) {
    console.error('Kanze: could not work out the size for a suggested piece.', error);
  }
}

/**
 * Points at a row whose size has not been chosen: its sizes give one short shake (the same cue
 * as on the product page, assets/product.js), and the page scrolls only when they are not on
 * screen. With the keyboard, focus goes to the row's first size.
 */
function nudge(row) {
  window.Kanze.shake(row);
  if (window.Kanze.keys) window.Kanze.focus(row.querySelector('input:not(:disabled)'));
}

/** What a row already says about its piece, for the confirmation while it waits for Shopify. */
function shownIn(row) {
  return { title: row.querySelector('.look-row__title')?.textContent.trim() || '', options: row.querySelector('input:checked')?.dataset.size || '', price: row.querySelector('.look-row__price .price')?.textContent.trim() || '' };
}

/**
 * Gives each row its own Add button, at the end of its line of sizes (the template from
 * snippets/look-row-add.liquid, inside `host`), so its piece can be added on its own.
 */
function addButtons(rows, host) {
  const sample = host.querySelector('template[data-add-button]')?.content.firstElementChild;
  if (!sample) return;
  for (const row of rows) {
    const sizes = row.querySelector('.look-row__sizes');
    if (!sizes || row.querySelector('[data-add-piece]')) continue;
    const button = sample.cloneNode(true);
    button.setAttribute('aria-label', button.dataset.label.replace('[title]', row.querySelector('.look-row__title')?.textContent.trim() || ''));
    const line = Object.assign(document.createElement('div'), { className: 'look-row__buy' });
    sizes.replaceWith(line);
    line.append(sizes, button);
  }
}

class CompleteLook extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-rows]');
    this.stack = this.querySelector('[data-stack]');
    this.ghosts = this.querySelector('[data-ghosts]');
    this.button = this.querySelector('[data-add]');
    this.words = this.button.querySelector('[data-words]') || this.button;
    this.status = this.querySelector('[data-status]');
    this.button.addEventListener('click', () => this.add());
    // A piece's own Add button (two pieces): that piece alone goes in the bag.
    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-add-piece]') : null;
      if (button) this.addOne(button.closest('[data-look-row]'), button);
    });
    // A size was chosen: the line under the pieces drops what it asked for.
    this.addEventListener('change', () => this.hint());
  }

  /**
   * The line under the pieces. With one piece it asks for its size ("Select a size") in a quiet
   * colour until one is chosen. With two it is quiet: either piece can be added alone, so no
   * size is asked for until an Add is pressed without one. It keeps its place either way.
   */
  hint() {
    const rows = [...this.list.children];
    const missing = rows.length === 1 && !rows[0].querySelector('input:checked');
    this.status.classList.remove('is-asking');
    this.status.textContent = missing ? this.status.dataset.one : '';
  }

  pick(data, taken) {
    this.index = data.index;
    this.items = recs('complete', context(data, { taken }), data);
  }

  /** Shopify's own "pairs well with" picks, when the rules found nothing. */
  async shopify() {
    const response = await fetch(`${config.recommendations}.json?product_id=${config.productId}&intent=complementary&limit=4`);
    const data = response.ok ? await response.json() : { products: [] };
    return data.products
      .map((product) => this.index.by[product.handle])
      .filter((p) => p && p.av && !bag.includes(p.h) && p.g === this.index.by[config.product]?.g)
      .slice(0, 2)
      .map((p) => ({ h: p.h, reason: 'shopify', score: 0, why: 'Shopify complementary' }));
  }

  /** How many rows there is room for: the ones on show, or the blocks holding their place. 0 is no room yet. */
  get room() {
    if (this.classList.contains('is-waiting')) return 0;
    return this.ghosts ? this.ghosts.children.length : this.list.children.length;
  }

  async draw() {
    let items = this.items;
    if (!items.length && config.productId) items = await this.shopify().catch(() => []);
    const key = items.map((item) => item.h).join();
    if (key === this.drawn) return;
    this.drawn = key;
    let rows = (await Promise.all(items.map((item) => lookRow(this.index, item).catch(() => null)))).filter(Boolean);
    if (this.drawn !== key) return;
    if (!rows.length) {
      this.empty();
      return;
    }
    // More rows than there is room for would push the page down in front of him.
    const box = this.stack.getBoundingClientRect();
    if (this.room && rows.length > this.room && box.bottom < window.innerHeight) rows = rows.slice(0, this.room);
    words(this, items, 'complete');
    this.words.textContent = rows.length > 1 ? this.button.dataset.both : this.button.dataset.one;
    this.button.disabled = false;
    // Two pieces: each gets its own Add, and the button under them adds both. One piece: that button is its Add.
    if (rows.length > 1) addButtons(rows, this);
    this.fill(rows, box.height);
    for (const row of rows) track(row, 'complete');
    whenSeen(this, 'recs:complete:page', () => {
      const shown = [...this.list.children];
      const first = shown[0]?.dataset.reason;
      count('Recommendation Shown', { job: 'complete', count: shown.length, reasons: reasonsIn(shown), product_handle: config.product || undefined });
      count('Worn With Seen', { product_handle: config.product || undefined, pieces_count: shown.length, origin: ['look', 'shopify'].includes(first) ? first : 'complement' });
    });
    // "Find my size" may choose a size in a row; the line then asks for what is left.
    Promise.all(rows.map((row) => suggestSize(row))).then(() => this.hint());
    this.hint();
    reveal(this, { tap: false });
  }

  /** Puts the rows where the blocks (or the rows before them) were, and fades them in. */
  fill(rows, kept) {
    const ghosts = this.ghosts;
    this.ghosts = null;
    this.stopWaiting?.();
    this.list.replaceChildren(...rows);
    this.classList.remove('is-loading', 'is-empty');
    fade(this.list, 0, 1);
    fade(this.querySelector('[data-line]'), 0, 1);
    if (!ghosts) {
      this.settle(kept);
      return;
    }
    fade(ghosts, 1, 0).then(() => {
      ghosts.remove();
      this.settle(kept);
    });
    ghosts.style.opacity = 0;
  }

  /**
   * The room may only change size when all of the change is below the screen. Fewer rows than
   * the room was kept for: the room stays as it was.
   */
  settle(kept) {
    this.stack.style.minBlockSize = '';
    const box = this.stack.getBoundingClientRect();
    if (kept - box.height > 1 && box.bottom < window.innerHeight) this.stack.style.minBlockSize = `${kept}px`;
  }

  /** Nothing to suggest. Below the screen the block closes; on screen its room stays, empty. */
  empty() {
    if (this.classList.contains('is-waiting') || this.classList.contains('is-empty')) return;
    const close = () => {
      this.classList.add('is-waiting');
      this.classList.remove('is-loading', 'is-empty');
      this.ghosts?.remove();
      this.ghosts = null;
      this.list.replaceChildren();
      this.stack.style.minBlockSize = '';
    };
    if (this.getBoundingClientRect().top >= window.innerHeight) {
      close();
      return;
    }
    this.classList.add('is-empty');
    this.button.disabled = true;
    this.stopWaiting = whenBelow(this, close);
  }

  /** Says something on the line under the pieces, in the sale colour: what is missing, or why an add failed. */
  say(message) {
    this.status.textContent = message;
    this.status.classList.toggle('is-asking', Boolean(message));
  }

  /** Adds the chosen size of every row. A row with no size chosen is pointed at instead. */
  async add() {
    if (this.button.getAttribute('aria-busy') === 'true') return;
    const rows = [...this.list.children];
    const missing = rows.find((row) => !row.querySelector('input:checked'));
    if (missing) {
      this.say(rows.length > 1 ? this.status.dataset.choose : this.status.dataset.one);
      nudge(missing);
      return;
    }
    // At the tap: the button says "Adding…" (its own words keep their place underneath, CSS),
    // the photos fly and the confirmation shows.
    this.button.setAttribute('aria-busy', 'true');
    this.hint();
    try {
      // The bag does not open: each photo flies to the bag icon, one after the other.
      await window.Kanze.cart.add(
        rows.map((row) => ({ id: Number(row.querySelector('input:checked').value), quantity: 1 })),
        { from: rows.map((row) => row.querySelector('.look-row__media img')), origin: this.button, shown: rows.map(shownIn), track: { source: 'complete_look', sizes: sizesIn(rows) } }
      );
      wornWithAdded(rows, this.index, true);
    } catch (error) {
      this.say(error.message);
    } finally {
      this.button.removeAttribute('aria-busy');
    }
  }

  /** Adds one piece on its own, from its own Add button. With no size chosen, its sizes are pointed at. */
  async addOne(row, button) {
    if (!row || button.getAttribute('aria-busy') === 'true') return;
    const chosen = row.querySelector('input:checked');
    if (!chosen) {
      this.say(this.status.dataset.one);
      nudge(row);
      return;
    }
    button.setAttribute('aria-busy', 'true');
    this.hint();
    try {
      await window.Kanze.cart.add([{ id: Number(chosen.value), quantity: 1 }], {
        from: [row.querySelector('.look-row__media img')],
        origin: button,
        shown: [shownIn(row)],
        track: { source: 'complete_look', sizes: sizesIn([row]) },
      });
      wornWithAdded([row], this.index, this.list.children.length === 1);
    } catch (error) {
      this.say(error.message);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

/**
 * `Worn With Added`: pieces from "Complete the look" that have just gone into the bag (the
 * tracking plan). `full` when every piece on show went in, with "Add both" or as the only one.
 */
function wornWithAdded(rows, index, full) {
  const prices = rows.map((row) => index?.by[row.dataset.handle]?.p).filter((price) => Number.isFinite(Number(price)));
  count('Worn With Added', {
    product_handle: config.product || undefined,
    pieces_added: rows.length,
    value_added: prices.length === rows.length ? window.Kanze.amount(prices.reduce((sum, price) => sum + Number(price), 0)) : undefined,
    full_look: full,
  });
}

/**
 * "Complete your order" in the bag: up to two pieces that go with what is in it, each with its
 * sizes and its own Add button. It is the last thing in the part of the bag that scrolls.
 */
class BagUpsell extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-rows]');
    this.line = this.querySelector('[data-line]');
    this.status = this.querySelector('[data-status]');
    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-add-piece]') : null;
      if (button) this.add(button.closest('[data-look-row]'), button);
    });
    // A size was chosen: the line that asked for it goes.
    this.addEventListener('change', () => this.say(''));
  }

  get bag() {
    return this.closest('cart-items');
  }

  pick(data, taken) {
    const state = this.bag.state;
    this.index = data.index;
    // The piece added last leads: Shopify lists the bag newest first.
    this.items = state.items.length ? recs('cart', context(data, { taken, product: null, gender: '', items: state.items.map((item) => ({ h: item.h, price: item.p })), total: state.total, threshold: state.threshold, last: state.items[0].h }), data) : [];
  }

  async draw() {
    const items = this.items;
    const key = items.map((item) => item.h).join();
    if (key === this.drawn) return;
    this.drawn = key;
    const rows = (await Promise.all(items.map((item) => lookRow(this.index, item).catch(() => null)))).filter(Boolean);
    if (this.drawn !== key) return;
    if (!rows.length) {
      this.classList.add('is-waiting');
      this.list.replaceChildren();
      return;
    }
    // The sizes and the Add button share one line.
    addButtons(rows, this);
    this.line.textContent = items.some((item) => item.note === 'free_delivery') ? this.line.dataset.free : this.line.dataset.pairs;
    this.list.replaceChildren(...rows);
    this.classList.remove('is-waiting');
    for (const row of rows) {
      track(row, 'cart');
      suggestSize(row);
    }
    whenSeen(this, 'recs:cart:bag', () => count('Recommendation Shown', { job: 'cart', count: this.list.children.length, reasons: reasonsIn(this.list.children) }));
  }

  say(message) {
    this.status.textContent = message;
    this.status.hidden = !message;
  }

  async add(row, button) {
    if (!row || button.getAttribute('aria-busy') === 'true') return;
    const chosen = row.querySelector('input:checked');
    if (!chosen) {
      this.say(this.status.dataset.choose);
      nudge(row);
      return;
    }
    button.setAttribute('aria-busy', 'true');
    this.say('');
    // Its place at the top of the list is kept from this tap on (assets/cart.js).
    const place = document.createElement('li');
    this.bag.arriving?.(place);
    try {
      await window.Kanze.cart.add([{ id: Number(chosen.value), quantity: 1 }], { from: [row.querySelector('.look-row__media img')], origin: button, track: { source: 'cart_upsell', sizes: sizesIn([row]) } });
    } catch (error) {
      place.remove();
      this.say(error.message);
      button.removeAttribute('aria-busy');
    }
  }
}

/**
 * <look-sheet>: the sheet a look opens in (snippets/look-sheet.liquid). A look's button
 * ([data-look-open], with the addresses of its pieces) fills it with one row per piece and
 * opens it when the rows are ready, so nothing arrives inside it afterwards. The rows are
 * asked for as the finger lands, before the tap is over. One button adds every piece: the
 * sheet stays until Shopify answers (a refusal is said in it), then closes, and the photo
 * flies to the bag as after every other add.
 */
class LookSheet extends HTMLElement {
  connectedCallback() {
    this.dialog = this.closest('dialog');
    this.list = this.querySelector('[data-rows]');
    this.heading = this.querySelector('[data-title]');
    this.button = this.querySelector('[data-add]');
    this.words = this.button.querySelector('[data-words]');
    this.status = this.querySelector('[data-status]');
    this.loads = new Map();
    this.button.addEventListener('click', () => this.add());
    this.addEventListener('change', () => this.hint());
    this.onPress = (event) => {
      const opener = event.target instanceof Element ? event.target.closest('[data-look-open]') : null;
      if (opener) this.load(opener).catch(() => {});
    };
    this.onClick = (event) => {
      const opener = event.target instanceof Element ? event.target.closest('[data-look-open]') : null;
      if (opener) this.open(opener);
    };
    document.addEventListener('pointerdown', this.onPress, { capture: true, passive: true });
    document.addEventListener('click', this.onClick);
    // What is in the bag has changed: sizes may have sold out. The rows are asked for again.
    document.addEventListener('cart:updated', () => this.loads.clear());
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onPress, { capture: true });
    document.removeEventListener('click', this.onClick);
  }

  /** The rows of a look, asked for once. A failed try is not kept. */
  load(opener) {
    const key = opener.dataset.pieces;
    if (!this.loads.has(key)) {
      const rows = Promise.all(key.split('|').filter(Boolean).map((address) => pieceRow(address).catch(() => null))).then((list) => list.filter(Boolean));
      this.loads.set(key, rows);
      rows.then((list) => list.length || this.loads.delete(key)).catch(() => this.loads.delete(key));
    }
    return this.loads.get(key);
  }

  async open(opener) {
    if (opener.getAttribute('aria-busy') === 'true' || this.dialog.open) return;
    opener.setAttribute('aria-busy', 'true');
    const rows = await this.load(opener).catch(() => []);
    opener.removeAttribute('aria-busy');
    // Nothing to offer (the address did not answer): the look's first piece is still a page.
    if (!rows.length) {
      window.location.assign(opener.dataset.pieces.split('|')[0]);
      return;
    }
    this.opener = opener;
    this.heading.textContent = opener.dataset.title;
    this.list.replaceChildren(...rows);
    this.words.textContent = rows.length > 1 ? this.button.dataset.both : this.button.dataset.one;
    this.status.classList.remove('is-asking');
    this.hint();
    await Promise.all(rows.map((row) => suggestSize(row)));
    this.hint();
    window.Kanze.openDialog(this.dialog, opener);
  }

  /** The line under the pieces asks for the sizes, quietly, until every piece has one. */
  hint() {
    const rows = [...this.list.children];
    const missing = rows.some((row) => !row.querySelector('input:checked'));
    this.status.classList.remove('is-asking');
    this.status.textContent = missing ? (rows.length > 1 ? this.status.dataset.choose : this.status.dataset.one) : '';
  }

  say(message) {
    this.status.textContent = message;
    this.status.classList.toggle('is-asking', Boolean(message));
  }

  async add() {
    if (this.button.getAttribute('aria-busy') === 'true') return;
    const rows = [...this.list.children];
    const missing = rows.find((row) => !row.querySelector('input:checked'));
    if (missing) {
      this.say(rows.length > 1 ? this.status.dataset.choose : this.status.dataset.one);
      nudge(missing);
      return;
    }
    this.button.setAttribute('aria-busy', 'true');
    this.hint();
    // The photos fly from the look's own picture on the page, once the sheet has closed.
    const picture = this.opener?.closest('.look-tile')?.querySelector('img') || null;
    try {
      await window.Kanze.cart.add(
        rows.map((row) => ({ id: Number(row.querySelector('input:checked').value), quantity: 1 })),
        { from: rows.map(() => picture), origin: this.button, shown: rows.map(shownIn), ready: () => this.dialog.close(), track: { source: 'complete_look', sizes: sizesIn(rows) } }
      );
      wornWithAdded(rows, this.index);
    } catch (error) {
      this.say(error.message);
    } finally {
      this.button.removeAttribute('aria-busy');
    }
  }
}

/** A row in the bag is worked out when the bag is on show and in the shape the row is for. */
function due(row) {
  // A row that is only wanted sometimes (the search pop-up's best sellers) says when.
  if (row.hasAttribute('data-on-demand')) return row.hasAttribute('data-wanted');
  const cart = row.closest('cart-items');
  if (!cart) return true;
  const drawer = cart.closest('dialog');
  if (drawer && !drawer.open) return false;
  return Boolean(cart.querySelector('.cart__state.is-empty')) === row.matches('recs-rail');
}

/** Where a row lives: on the page, in the bag, or in a place of its own (`data-recs-place`). */
const place = (row) => (row.closest('cart-items') ? 'bag' : row.closest('[data-recs-place]')?.dataset.recsPlace || 'page');

/**
 * Works out the rows of the page, top to bottom, so a product shows in one row only. The rows
 * in the bag are worked out apart from the page's, when the bag is on show; so are the rows
 * of any other place (the search pop-up), when that place asks with a `recs:wanted` event.
 * @param {string} [where] - "page", "bag" or a `data-recs-place`
 */
async function refresh(where = 'page') {
  const rows = [...document.querySelectorAll('complete-look, recs-rail, bag-upsell')].filter((row) => place(row) === where && due(row));
  if (!rows.length) return;
  const data = { index: await getIndex(), signals: signals.read() };
  const taken = new Set();
  for (const row of rows) row.pick(data, taken);
  for (const row of rows) row.draw();
}

if (config) {
  customElements.define('recs-rail', RecsRail);
  customElements.define('complete-look', CompleteLook);
  customElements.define('bag-upsell', BagUpsell);
  customElements.define('look-sheet', LookSheet);
  signals.watch({ product: config.product, bag });
  document.addEventListener('cart:updated', (event) => {
    bag = [...new Set((event.detail?.cart?.items || []).map((item) => item.handle))];
    refresh().catch(() => {});
    refresh('bag').catch(() => {});
  });
  document.addEventListener('collection:change', () => refresh().catch(() => {}));
  document.addEventListener('recs:wanted', (event) => refresh(event.detail?.place || 'page').catch(() => {}));
  document.addEventListener('dialog:open', (event) => {
    if (event.target.id === 'CartDrawer') refresh('bag').catch(() => {});
  });
  // Without the index there is nothing to suggest: a block that kept room for rows gives it up.
  refresh().catch(() => document.querySelectorAll('complete-look').forEach((row) => row.empty()));
  refresh('bag').catch(() => {});
}
